import { effect, inject, Injectable, Injector } from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';

import { filter } from 'rxjs/operators';

import { environment } from '../../../environments/environment';

import { SwUpdate } from '@angular/service-worker';
import {
  ModalUpdateVersionComponent,
  ModalUpdateVersionData,
} from '../../shared/modals/modal-update-version/modal-update-version.component';
import {
  isFrontAppVersionUpToDate,
  LastDeployedVersions,
} from '../../shared/models/schemas/front-app-version.schema';
import { EnvironmentService } from './environment.service';
import { IsApplicationService } from './is-application.service';
import { LastDeployedVersionsService } from './last-deployed-versions.service';
import { ModalService } from './modal.service';

@Injectable({
  providedIn: 'root',
})
export class FrontAppVersionSiteService {
  private injector = inject(Injector);
  private router = inject(Router);
  private swUpdate = inject(SwUpdate);
  private environmentService = inject(EnvironmentService);
  private isApplicationService = inject(IsApplicationService);
  private lastDeployedVersionsService = inject(LastDeployedVersionsService);
  private modalService = inject(ModalService);

  newVersionAvailable = false;
  newBreakingVersionAvailable = false;
  newVersionAvailableDismissed = false;

  init() {
    if (!this.isApplicationService.isApplication() && environment.production) {
      // attendre 20s pour laisser le temps au service worker de télécharger la nouvelle version
      setTimeout(() => {
        effect(
          () => {
            const lastDeployedVersions = this.lastDeployedVersionsService.lastDeployedVersions();
            this.checkNewVersionAvailable(lastDeployedVersions);
          },
          { injector: this.injector }
        );
      }, 20 * 1000);

      // affichage des popups seulement aux navigations pour éviter à l'utilisateur de le stopper au milieu de quelque chose s'il y a une nouvelle version
      this.router.events.pipe(filter((event) => event instanceof NavigationEnd)).subscribe(() => {
        if (this.newBreakingVersionAvailable) {
          this.openVersionUpdate(true);
        } else if (this.newVersionAvailable && !this.newVersionAvailableDismissed) {
          this.openVersionUpdate(false);
          this.newVersionAvailableDismissed = true;
        }
      });

      if (this.swUpdate.isEnabled) {
        this.swUpdate.versionUpdates
          .pipe(filter((versionEvent) => versionEvent.type === 'VERSION_READY'))
          .subscribe(() => {
            this.newVersionAvailable = true;
          });
      }
    }
  }

  checkNewVersionAvailable(lastDeployedVersions: LastDeployedVersions | null) {
    if (!lastDeployedVersions) return;
    if (
      lastDeployedVersions.lastBreakingVersion &&
      !isFrontAppVersionUpToDate(
        this.environmentService.frontAppVersion,
        lastDeployedVersions.lastBreakingVersion
      )
    ) {
      this.newBreakingVersionAvailable = true;
    } else if (
      !isFrontAppVersionUpToDate(
        this.environmentService.frontAppVersion,
        lastDeployedVersions.lastVersion
      )
    ) {
      this.newVersionAvailable = true;
    }
  }

  openVersionUpdate(force: boolean) {
    const modalData: ModalUpdateVersionData = {
      forceUpdate: force,
      lastDeployedVersions: this.lastDeployedVersionsService.lastDeployedVersions(),
    };

    this.modalService.openNewDesign(ModalUpdateVersionComponent, modalData, {
      disableClose: force,
    });
  }
}
